import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import exportIcon from "../assets/export.svg";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import pdfIcon from "../assets/pdf.svg";
import { centerOptions } from "../contants/options";

function InvoiceTables({
  data,
  setIsLoading,
  isLoading,
  filters,
  selectedDoctor,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [paginatedData, setPaginatedData] = useState([]);
  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page on change
  };

  // Get the current records for the page
  useEffect(() => {
    setIsLoading(true);
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = data?.slice(indexOfFirstRecord, indexOfLastRecord);
    setPaginatedData(currentRecords);
    setIsLoading(false);
  }, [currentPage, recordsPerPage, data]);

  // Calculate total pages
  const totalPages = Math.ceil(data?.length / recordsPerPage);

  const handleExport = () => {
    setIsLoading(true); // Show loading icon

    // Process date fields
    const formatDate = (date) =>
      date ? date.split("T")[0].split("-").reverse().join("-") : null;

    // Processed data
    const modifiedData = data.map((item) => ({
      ...item,
      DOD: formatDate(item["DOD"]),
      DOA: formatDate(item["DOA"]),
      "Added on": formatDate(item["Added on"]),
      "Total Amount": parseFloat(item["Total Amount"]) || 0,
      "Total Discounts": parseFloat(item["Total Discounts"]) || 0,
      "Grand Total": parseFloat(item["Grand Total"]) || 0,
      TDS: parseFloat(item["TDS"]) || 0,
      "Doctor Net": parseFloat(item["Doctor Net"]) || 0,
      "Invoice Total Billing": parseFloat(item["Invoice Total Billing"]) || 0,
      "Invoice Total Collected":
        parseFloat(item["Invoice Total Collected"]) || 0,
      "Invoice TDS": parseFloat(item["Invoice TDS"]) || 0,
      "General Discount": parseFloat(item["General Discount"]) || 0,
      "Insurance Discount": parseFloat(item["Insurance Discount"]) || 0,
      "Itemized Discount": parseFloat(item["Itemized Discount"]) || 0,
      "Total Discount": parseFloat(item["Total Discount"]) || 0,
      "Invoice Total Balance": parseFloat(item["Invoice Total Balance"]) || 0,
      Quantity: parseFloat(item["Quantity"]) || 0,
    }));

    // Filter data for Payable & Pending sheets
    const payableData = modifiedData.filter(
      (item) => item["Invoice Paid / Unpaid"] === "Payable"
    );
    const pendingData = modifiedData.filter(
      (item) => item["Invoice Paid / Unpaid"] === "Pending"
    );

    // Calculate total sum for specified columns in each sheet
    const calculateTotalRow = (data) => {
      const totals = {
        "Total Amount": 0,
        "Total Discounts": 0,
        "Grand Total": 0,
        TDS: 0,
        "Doctor Net": 0,
        "Invoice Total Billing": 0,
        "Invoice Total Collected": 0,
        "Invoice TDS": 0,
        "General Discount": 0,
        "Insurance Discount": 0,
        "Itemized Discount": 0,
        "Total Discount": 0,
        "Invoice Total Balance": 0,
        Quantity: 0,
      };

      for (const item of data) {
        for (const key in totals) {
          totals[key] += item[key] || 0;
        }
      }

      const totalRow = Object.fromEntries(
        Object.keys(data[0] || {}).map((key) => [key, ""])
      );

      Object.assign(totalRow, totals);
      totalRow["Primary Doctor"] = "Total";

      return totalRow;
    };

    // Add total row to each dataset
    if (modifiedData.length) modifiedData.push(calculateTotalRow(modifiedData));
    if (payableData.length) payableData.push(calculateTotalRow(payableData));
    if (pendingData.length) pendingData.push(calculateTotalRow(pendingData));

    // Function to calculate column widths based on content
    const getColumnWidths = (data) => {
      if (!data.length) return [];

      return Object.keys(data[0]).map((key) => ({
        wch:
          Math.max(
            key.length, // Header length
            ...data.map((row) => (row[key] ? row[key].toString().length : 0)) // Max content length
          ) + 2, // Add padding
      }));
    };

    // Create Excel sheets
    const wb = XLSX.utils.book_new();

    const createSheet = (data, sheetName) => {
      const ws = XLSX.utils.json_to_sheet(data);
      ws["!cols"] = getColumnWidths(data); // Set column widths
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    };

    createSheet(modifiedData, "Master");
    createSheet(payableData, "Payable");
    createSheet(pendingData, "Pending");

    // Save file
    XLSX.writeFile(
      wb,
      `IPD_${data[0]["Primary Doctor"]} - ${data[0]["Branch"]} - ${filters.startDate} - ${filters.endDate}.xlsx`
    );

    setIsLoading(false); // Hide loading icon
  };

  const handleExportPDF = () => {
    setIsLoading(true); // Show loading icon

    const formatDate = (date) =>
      date ? date.split("T")[0].split("-").reverse().join("-") : "";

    const modifiedData = data.map((item) => ({
      "IPD Number": item["IPD id"],
      "Patient Name": item["Patient Name"],
      DOA: formatDate(item["DOA"]),
      DOD: formatDate(item["DOD"]),
      "Primary Doctor": item["Primary Doctor"],
      "Department/Surgery": item["Department/Surgery"],
      Quantity: item["Quantity"] || 0,
      "Total Amount": parseFloat(item["Total Amount"]) || 0,
      "Total Discounts": parseFloat(item["Total Discounts"]) || 0,
      "Grand Total": parseFloat(item["Grand Total"]) || 0,
      TDS: parseFloat(item["TDS"]) || 0,
      "Doctor Net": parseFloat(item["Doctor Net"]) || 0,
    }));

    // Calculate total sum for "Total" column
    const totalSum = modifiedData.reduce(
      (sum, item) => sum + item["Total Amount"],
      0
    );
    const totalDiscounts = modifiedData.reduce(
      (sum, item) => sum + item["Total Discounts"],
      0
    );
    const totalGrandTotal = modifiedData.reduce(
      (sum, item) => sum + item["Grand Total"],
      0
    );
    const totalTDS = modifiedData.reduce((sum, item) => sum + item["TDS"], 0);
    const totalDoctorNet = modifiedData.reduce(
      (sum, item) => sum + item["Doctor Net"],
      0
    );

    // Initialize jsPDF
    const doc = new jsPDF({ orientation: "landscape" });
    console.log(selectedDoctor, filters?.centers);

    // Title
    doc.setFontSize(14);
    doc.text(`Doctor Name: ${selectedDoctor?.["Doctor Name"]}`, 14, 15);
    doc.text(
      `Center Name: ${
        filters?.centers?.find(
          (center) => center.value === selectedDoctor?.CenterId.toString()
        )?.label ||
        centerOptions?.find(
          (center) => center.value === selectedDoctor?.CenterId.toString()
        )?.label
      }`,
      14,
      25
    );
    doc.text(
      `Date From: ${formatDate(filters?.startDate)} --- Date To: ${formatDate(
        filters?.endDate
      )}`,
      14,
      35
    );
    doc.text(`Generated On: ${new Date()}`, 14, 45);

    // Prepare table columns
    // Prepare table columns
    const columns = [
      "IPD Number",
      "Patient Name",
      "DOA",
      "DOD",
      "Primary Doctor",
      "Department/Surgery",
      "Quantity",
      "Total Amount",
      "Consc.",
      "Grand Total",
      "TDS",
      "Doctor Net",
    ];

    // Convert data to rows
    const rows = modifiedData.map((row) => Object.values(row));

    // Append total row
    rows.push([
      "",
      "",
      "",
      "",
      "",
      "Total",
      "",
      totalSum.toFixed(2),
      totalDiscounts.toFixed(2),
      totalGrandTotal.toFixed(2),
      totalTDS.toFixed(2),
      totalDoctorNet.toFixed(2),
    ]);

    // Generate table
    autoTable(doc, {
      head: [columns], // Table headers
      body: rows,
      startY: 55,
      styles: { fontSize: 10 },
      theme: "grid",
      headStyles: {
        fillColor: "#003366", // Header background color
        textColor: "#fff", // Header text color
        fontSize: 12, // Header font size
      },
      bodyStyles: {
        fontSize: 10, // Regular body font size
      },
    });

    const summaryStartY = doc.lastAutoTable.finalY + 10; // Get the position after the table

    // Set bold font for summary labels
    doc.setFont("helvetica", "bold");

    // Summary labels in the bottom-right corner
    doc.text("Total Amount:", 160, summaryStartY);
    doc.text("Total Consession:", 160, summaryStartY + 10);
    doc.text("Grand Total:", 160, summaryStartY + 20);
    doc.text("TDS:", 160, summaryStartY + 30);
    doc.text("Doctor Net:", 160, summaryStartY + 40);

    // Set regular font for values
    doc.setFont("helvetica", "normal");

    // Summary values
    doc.text(totalSum.toFixed(2), 200, summaryStartY);
    doc.text(totalDiscounts.toFixed(2), 200, summaryStartY + 10);
    doc.text(totalGrandTotal.toFixed(2), 200, summaryStartY + 20);
    doc.text(totalTDS.toFixed(2), 200, summaryStartY + 30);
    doc.text(totalDoctorNet.toFixed(2), 200, summaryStartY + 40);
    // Save file
    doc.save(
      `IPD_${data[0]["Primary Doctor"]} - ${data[0]["Branch"]} - ${filters.startDate} - ${filters.endDate}.pdf`
    );

    setIsLoading(false); // Hide loading icon
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#f9fafb",
        borderRadius: "8px",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "15px",
          marginBottom: "20px",
        }}
      >
        <img
          src={exportIcon}
          alt="excel"
          style={{ width: "40px", height: "40px", cursor: "pointer" }}
          title="Export to Excel"
          onClick={handleExport}
        />

        <img
          src={pdfIcon}
          alt="pdf"
          style={{ width: "40px", height: "40px", cursor: "pointer" }}
          title="Export to PDF"
          onClick={handleExportPDF}
        />
      </div>

      {/* Records per page selector */}
      <div style={{ marginBottom: "20px" }}>
        <label style={{ fontSize: "16px", fontWeight: "600" }}>
          Records per page:{" "}
        </label>
        <select
          onChange={handleRecordsPerPageChange}
          value={recordsPerPage}
          style={{
            padding: "5px 10px",
            borderRadius: "5px",
            border: "1px solid #ddd",
            fontSize: "16px",
          }}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={data?.length}>All</option>
        </select>
      </div>

      {/* Table */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#003366", color: "#fff" }}>
            {/* Column Headers */}
            <th style={{ padding: "12px 15px", textAlign: "left" }}>IPD Id</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Invoice Id
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>Item No</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>Year</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>Month</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>Branch</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>DOD</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>DOA</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Patient Name
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Referred By
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Primary Doctor
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Department/ Surgery Name
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>TPA</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Added on
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Quantity
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Total Amount
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Total Discount
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Grand Total
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>TDS</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>Net</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Invoice Total Billing
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Invoice Total Collected
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>TDS</th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              General Discount
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Insurance Discount
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Itemized Discount
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Total Discount
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Invoice Total Balance
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Fully Collected
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Invoice Paid / Unpaid
            </th>
            <th style={{ padding: "12px 15px", textAlign: "left" }}>
              Entered By
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="17" style={{ textAlign: "center", padding: "20px" }}>
                <span>Loading...</span>
              </td>
            </tr>
          ) : paginatedData?.length > 0 ? (
            paginatedData.map((item, index) => (
              <tr
                key={item.ipdId}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f9fafb" : "#d3dfeb", // Subtle dual-tone styling
                  transition: "background-color 0.3s ease",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = "#e2e8f0")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    index % 2 === 0 ? "#f9fafb" : "#d3dfeb")
                }
              >
                <td style={{ padding: "12px 15px" }}>{item["IPD id"]}</td>
                <td style={{ padding: "12px 15px" }}>{item["Invoice Id"]}</td>
                <td style={{ padding: "12px 15px" }}>{item["Unique id"]}</td>
                <td style={{ padding: "12px 15px" }}>{item["Year"]}</td>
                <td style={{ padding: "12px 15px" }}>{item["Month"]}</td>
                <td style={{ padding: "12px 15px" }}>{item["Branch"]}</td>
                <td style={{ padding: "12px 15px" }}>
                  {item["DOD"].split("T")[0].split("-").reverse().join("-")}
                </td>
                <td style={{ padding: "12px 15px" }}>
                  {item["DOA"].split("T")[0].split("-").reverse().join("-")}
                </td>
                <td style={{ padding: "12px 15px" }}>{item["Patient Name"]}</td>
                <td style={{ padding: "12px 15px" }}>{item["Referred By"]}</td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Primary Doctor"]}
                </td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Department/Surgery"]}
                </td>
                <td style={{ padding: "12px 15px" }}>{item["TPA"]}</td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Added on"]
                    .split("T")[0]
                    .split("-")
                    .reverse()
                    .join("-")}
                </td>
                <td style={{ padding: "12px 15px" }}>{item["Quantity"]}</td>
                <td style={{ padding: "12px 15px" }}>{item["Total Amount"]}</td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Total Discounts"]}
                </td>
                <td style={{ padding: "12px 15px" }}>{item["Grand Total"]}</td>
                <td style={{ padding: "12px 15px" }}>{item["TDS"]}</td>
                <td style={{ padding: "12px 15px" }}>{item["Doctor Net"]}</td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Invoice Total Billing"]}
                </td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Invoice Total Collected"]}
                </td>
                <td style={{ padding: "12px 15px" }}>{item["Invoice TDS"]}</td>
                <td style={{ padding: "12px 15px" }}>
                  {item["General Discount"]}
                </td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Insurance Discount"]}
                </td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Itemized Discount"]}
                </td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Total Discount"]}
                </td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Invoice Total Balance"]}
                </td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Fully Collected"]}
                </td>
                <td style={{ padding: "12px 15px" }}>
                  {item["Invoice Paid / Unpaid"]}
                </td>
                <td style={{ padding: "12px 15px" }}>{item["Entered By"]}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="17" style={{ textAlign: "center" }}>
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            style={{
              padding: "8px 15px",
              margin: "0 5px",
              backgroundColor: "#003366",
              color: "#fff",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: "8px 15px",
              margin: "0 5px",
              backgroundColor: "#003366",
              color: "#fff",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Prev
          </button>
          {[...Array(totalPages).keys()].map((page) => (
            <button
              key={page + 1}
              onClick={() => handlePageChange(page + 1)}
              disabled={currentPage === page + 1}
              style={{
                padding: "8px 15px",
                margin: "0 5px",
                backgroundColor:
                  currentPage === page + 1 ? "#003366" : "#e6e6e6",
                color: currentPage === page + 1 ? "#fff" : "#000",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              {page + 1}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: "8px 15px",
              margin: "0 5px",
              backgroundColor: "#003366",
              color: "#fff",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              padding: "8px 15px",
              margin: "0 5px",
              backgroundColor: "#003366",
              color: "#fff",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
}

export default InvoiceTables;
